/**
 * Service Worker Registration for TaskFly PWA
 * Handles registration, updates, and error handling for the service worker
 * Version: 2.0.0 - Enhanced with manifest change detection
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onManifestChange?: () => void;
  onInstall?: () => void;
};

// Track if we've already prompted for reload
let hasPromptedReload = false;

export function register(config?: Config): void {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(import.meta.env.BASE_URL, window.location.href);
    
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = '/sw.js';

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });

    // Handle PWA install prompt
    let deferredPrompt: BeforeInstallPromptEvent | null = null;
    
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      
      // Notify app that install is available
      window.dispatchEvent(new CustomEvent('pwa-install-available'));
      
      // Store for later use
      (window as any).deferredPwaPrompt = deferredPrompt;
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt = null;
      (window as any).deferredPwaPrompt = null;
      
      // Clear the prompt reference
      window.dispatchEvent(new CustomEvent('pwa-installed'));
    });
    
    // Handle messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, version: _version } = event.data || {};
        
        switch (type) {
          case 'SW_ACTIVATED':
            break;
            
          case 'MANIFEST_CHANGED':
            handleManifestChange(config);
            break;
        }
      });
    }
  }
}

// Handle manifest changes - force reload to update PWA
function handleManifestChange(config?: Config): void {
  if (hasPromptedReload) return;
  hasPromptedReload = true;
  
  // Call the manifest change callback if provided
  config?.onManifestChange?.();
  
  // Store a flag in localStorage to indicate we need to clear old caches
  try {
    localStorage.setItem('pwa_manifest_changed', Date.now().toString());
  } catch (e) {
    // localStorage not available
  }
  
  // Show persistent banner for critical SW update instead of blocking confirm()
  const banner = document.createElement('div');
  banner.style.cssText = 'position:fixed;bottom:0;left:0;right:0;z-index:99999;background:#dc2626;color:white;padding:12px 20px;display:flex;align-items:center;justify-content:space-between;font-family:system-ui;box-shadow:0 -2px 12px rgba(0,0,0,0.2);';
  banner.innerHTML = `
    <span>A critical update is available. The app will reload in <span id="sw-update-countdown">10</span> seconds.</span>
    <div style="display:flex;gap:8px;">
      <button id="sw-update-now" style="background:white;color:#dc2626;border:none;padding:6px 16px;border-radius:6px;font-weight:600;cursor:pointer;font-size:14px;">Reload Now</button>
      <button id="sw-update-later" style="background:transparent;color:white;border:1px solid rgba(255,255,255,0.5);padding:6px 12px;border-radius:6px;cursor:pointer;font-size:14px;">Later</button>
    </div>
  `;
  document.body.appendChild(banner);
  let countdown = 10;
  const countdownEl = document.getElementById('sw-update-countdown');
  const timer = setInterval(() => {
    countdown--;
    if (countdownEl) countdownEl.textContent = String(countdown);
    if (countdown <= 0) {
      clearInterval(timer);
      window.location.reload();
    }
  }, 1000);
  document.getElementById('sw-update-now')?.addEventListener('click', () => { clearInterval(timer); window.location.reload(); });
  document.getElementById('sw-update-later')?.addEventListener('click', () => { clearInterval(timer); banner.remove(); });
}

async function registerValidSW(swUrl: string, config?: Config): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: '/'
    });

    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      
      if (!installingWorker) return;

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            config?.onUpdate?.(registration);
            
            // Automatically prompt for update in standalone mode
            if (isPwa()) {
              promptUpdate(registration);
            }
          } else {
            config?.onSuccess?.(registration);
          }
        }
      };
    };

    // Check for updates periodically
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000); // Check every hour

    // Also check when the page becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration.update();
      }
    });

  } catch (error) {
    console.error('[PWA] Error during service worker registration:', error);
  }
}

// Prompt user to update the app
function promptUpdate(registration: ServiceWorkerRegistration): void {
  if (hasPromptedReload) return;
  
  if (registration.waiting) {
    // Send message to SW to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
  
  // Listen for the new service worker to take control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hasPromptedReload) {
      hasPromptedReload = true;
      window.location.reload();
    }
  });
}

async function checkValidServiceWorker(swUrl: string, config?: Config): Promise<void> {
  try {
    const response = await fetch(swUrl, {
      headers: { 'Service-Worker': 'script' },
    });

    const contentType = response.headers.get('content-type') || '';
    const isValidSW = response.status === 200 && contentType.includes('javascript');

    if (!isValidSW) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.unregister().then(() => {
          window.location.reload();
        });
      });
      return;
    }

    await registerValidSW(swUrl, config);
  } catch {
  }
}

export function unregister(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
      .then((registration) => {
        return registration.unregister();
      })
      .then((success) => {
        if (success) {
        }
        return success;
      })
      .catch((error) => {
        console.error('[PWA] Error unregistering service worker:', error);
        return false;
      });
  }
  return Promise.resolve(false);
}

export function updateServiceWorker(): Promise<void> {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
      .then((registration) => {
        return registration.update();
      })
      .then(() => {
      });
  }
  return Promise.resolve();
}

// Clear all caches and force reload - useful for troubleshooting
export async function clearCachesAndReload(): Promise<void> {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
  }
  
  // Unregister service worker
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.unregister();
  }
  
  // Clear localStorage flags
  try {
    localStorage.removeItem('pwa_manifest_changed');
  } catch (e) {
    // ignore
  }
  
  window.location.reload();
}

// Check if app is running as PWA
export function isPwa(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true ||
         document.referrer.includes('android-app://');
}

// Check if app can be installed
export function canInstall(): boolean {
  return (window as any).deferredPwaPrompt !== null;
}

// Trigger install prompt
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const deferredPrompt = (window as any).deferredPwaPrompt as BeforeInstallPromptEvent | null;
  
  if (!deferredPrompt) {
    return null;
  }

  try {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    return outcome;
  } catch (error) {
    console.error('[PWA] Error prompting install:', error);
    return null;
  }
}

// Get the current service worker version
export async function getServiceWorkerVersion(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const activeWorker = registration.active;
    if (activeWorker) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.version || null);
        };
        activeWorker.postMessage(
          { type: 'GET_VERSION' },
          [messageChannel.port2]
        );
        
        // Timeout after 1 second
        setTimeout(() => resolve(null), 1000);
      });
    }
  } catch (e) {
    console.error('[PWA] Error getting SW version:', e);
  }
  return null;
}